export const PENDING_CONNECTION_KEY = "od:pendingConnectionString";

let memory: string | null = null;

function parsePayload(raw: string): { connectionString: string; type?: string } | null {
  const text = raw.trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text) as {
      connectionString?: string;
      type?: string;
    };
    if (parsed?.connectionString?.trim()) {
      return {
        connectionString: parsed.connectionString.trim(),
        type: parsed.type,
      };
    }
  } catch {
    if (text.includes("://") || text.startsWith("postgresql")) {
      return { connectionString: text };
    }
  }
  return null;
}

function writeStores(serialized: string) {
  memory = serialized;
  try {
    sessionStorage.setItem(PENDING_CONNECTION_KEY, serialized);
  } catch {
    /* private mode */
  }
  try {
    localStorage.removeItem(PENDING_CONNECTION_KEY);
  } catch {
    /* ignore leftover from older builds */
  }
}

export function stashPendingConnection(payload: {
  connectionString: string;
  type?: string;
}) {
  writeStores(JSON.stringify(payload));
}

export function stashPendingConnectionFromWindowName() {
  if (typeof window === "undefined") return;
  const raw = window.name;
  const payload = parsePayload(raw);
  if (payload) {
    writeStores(JSON.stringify(payload));
  }
  if (raw) {
    window.name = "";
  }
}

export function peekPendingConnection(): {
  connectionString: string;
  type?: string;
} | null {
  const candidates = [
    memory,
    (() => {
      try {
        return sessionStorage.getItem(PENDING_CONNECTION_KEY);
      } catch {
        return null;
      }
    })(),
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    const parsed = parsePayload(raw);
    if (parsed) return parsed;
  }
  return null;
}

/**
 * True when a pending source is a Google Sheet, whatever shape the hand-off
 * used: the website may send `type: "google-sheets"` or only the sheet URL.
 * Guests may not connect a Sheet (board answer Q8), so `#/setup` needs this
 * before it creates a guest session. Postgres strings keep the guest path.
 */
export function isPendingSourceGoogleSheet(): boolean {
  const pending = peekPendingConnection();
  if (!pending) return false;
  const type = (pending.type ?? "").toLowerCase();
  if (type.includes("sheet")) return true;
  const value = pending.connectionString;
  if (/\/spreadsheets\/d\//.test(value)) return true;
  return /^(docs\.google\.com\/spreadsheets|spreadsheet:)/i.test(value);
}

export function clearPendingConnection() {
  memory = null;
  try {
    sessionStorage.removeItem(PENDING_CONNECTION_KEY);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(PENDING_CONNECTION_KEY);
  } catch {
    /* ignore leftover from older builds */
  }
}
